// @flow
import React, { useCallback, useState, useEffect, useRef } from "react";
import { Segment, Divider, Button, Transition, Card, Icon, Step, List } from "semantic-ui-react";
import { useSelector, useDispatch } from "react-redux";
import { getFormValues } from "redux-form";
import { useTranslation, withTranslation } from "react-i18next";
import classnames from "classnames";

import PostalCodeAndLocation from "modules/Reminder/Creation/components/Steps/PostCodeAndSelectable";

import { ConsumptionField, ProviderSelectField, RadioBtnsEcoTariff } from "./Inputs/Inputs";
import { fromSlug, CategoryTypes, CategoryIcon } from "Commons/Modules/category";
import { getUser } from "redux/sagas/selectors";
import { HOUSE, APT, COMPANY } from "Commons/Utilities/Constants/PropertyTypes";
import { GoogleTagManager, isMobile } from "Commons/Utilities";
import { createShortReminder } from "modules/Reminder/redux/shortReminders/actions";
import IframeRegisterStep from "./IframeRegisterStep";
import type { ShortOfferFormTransitionPropsT } from "../misc/Types";
import {
  LOCATION,
  CATEGORY,
  CONFIRMATION,
  CONSUMPTION,
  IN,
  OUT,
  PROPERTY,
  PROVIDER,
  REGISTRATION,
  YES_ECO_TARIFF,
} from "../misc/constants";
import IframeRegistrationSuccessMessage from "Commons/Modules/auth/components/Register/IframeRegistrationSuccessMessage";
import { normalizeData } from "../misc/Utils";
import { getTrackingData } from "Commons/Utilities/Tracking/TrackingHandler";
import {
  InternalDataProtectionRoute,
  InternalImpressumRoute,
  InternalUserSomethingRoute,
} from "modules/ContentPages/misc/ContentPageRoutes";

import Styles from "./ShortOfferIframe.module.scss";

const ConfirmationMessage = withTranslation("reminderCreationPage")(IframeRegistrationSuccessMessage);
const transitionProps = { unmountOnHide: true, animation: "fade left", duration: 300 };

function IframeTransitionForm(props: ShortOfferFormTransitionPropsT) {
  const {
    id,
    categorySlug,
    change,
    providers,
    valid,
    invalid,
    reset,
    STEPS,
    onCategoryChange,
    pristine,
    resetSection,
    isAnon,
  } = props;

  const categoryType = fromSlug(categorySlug);
  const isCategoryGas = categoryType === CategoryTypes.GAS;
  const consumptionFieldName = isCategoryGas ? "gasYearlyConsumption" : "electricityYearlyConsumption";
  const ecoTariffFieldName = isCategoryGas ? "gasDesiredGreenPower" : "electricityDesiredGreenPower";
  const categoryEndpoint = isCategoryGas ? "gas" : "electricity";

  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const currentValues = useSelector((state) => getFormValues("shortOffer")(state)) || {};
  const isCompany = currentValues.propertyType === COMPANY;

  let initialStep = { step: STEPS.PROPERTY.id, stage: IN };

  const { t } = useTranslation("blogArticle");
  const [currentStep, setCurrentStep] = useState(initialStep);

  const getNextStep = (currentStep) => {
    return {
      step: STEPS[currentStep.step].nextStep,
      stage: IN,
    };
  };

  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  });

  useEffect(() => {
    reset();
  }, [reset]);

  // Use for Register form or already logged in
  const memoizedHandleAuthenticate = useCallback(
    (userId, alreadyLoggedIn = false) => {
      const user = userRef.current;
      const trackingInfo: any = getTrackingData();
      if (!user.user?.id && !userId) {
        throw new Error("Undefined userId when creating ShortOfferReminder");
      }
      dispatch(
        createShortReminder(user.user?.id || userId, {
          ...normalizeData(currentValues),
          ...trackingInfo,
          signInUrl: window.location.pathname,
        })
      );

      if (!alreadyLoggedIn) {
        setCurrentStep((currentStep) => ({
          ...currentStep,
          stage: OUT,
        }));
      } else {
        setCurrentStep({ step: CONFIRMATION, stage: IN });
      }
    },
    [dispatch, userRef, currentValues]
  );

  useEffect(() => {
    return function () {
      reset();
    };
  }, [reset]);

  const showNextButton = currentStep.step !== STEPS.REGISTRATION.id && currentStep.step !== STEPS.CONFIRMATION.id;

  let disableNextButton = currentStep.step === STEPS.PROPERTY.id || currentStep.step === STEPS.CATEGORY.id;

  return (
    <Segment.Group className={Styles.shortOfferSegments}>
      <Segment id={id} data-cy={"shortOfferForm"} raised className={Styles.shortOfferSegment}>
        <Transition
          {...transitionProps}
          reactKey={PROPERTY}
          visible={currentStep.step === PROPERTY && currentStep.stage === IN}
          onHide={() => {
            setCurrentStep(getNextStep(currentStep));
          }}
        >
          <div className={Styles.flexGrow}>
            <div className={Styles.stepContent}>
              <h3 className={Styles.title}>
                {t("blogArticleUnfolding:formFieldDescriptors.propertyType.description")}
              </h3>
              <h3 className={Styles.subheadline}>
                {t("blogArticleUnfolding:formFieldDescriptors.propertyType.subheadline")}
              </h3>
              <div className={Styles.cards}>
                <Card
                  data-cy="transition-form-property-house"
                  className={classnames(Styles.card, "card-border")}
                  onClick={() => {
                    change("propertyType", HOUSE);
                    setCurrentStep({ ...currentStep, stage: OUT });
                    GoogleTagManager.shortOfferPropertyTypeClick(HOUSE);
                  }}
                >
                  <Card.Content className={Styles["card-content"]}>
                    <Card.Header textAlign={"center"} className={Styles["card-header"]}>
                      {t("blogArticleUnfolding:formFieldDescriptors.propertyType.house")}
                    </Card.Header>
                    <Icon name="home" size={"huge"} className={classnames(Styles.cardIcon, "theme-color-primary")} />
                  </Card.Content>
                </Card>
                <Card
                  data-cy="transition-form-property-apartment"
                  className={classnames(Styles.card, "card-border")}
                  onClick={() => {
                    change("propertyType", APT);
                    setCurrentStep({ ...currentStep, stage: OUT });
                    GoogleTagManager.shortOfferPropertyTypeClick(APT);
                  }}
                >
                  <Card.Content className={Styles["card-content"]}>
                    <Card.Header textAlign={"center"} className={Styles["card-header"]}>
                      {t("blogArticleUnfolding:formFieldDescriptors.propertyType.apt")}
                    </Card.Header>
                    <Icon
                      name="building"
                      size={"huge"}
                      className={classnames(Styles.cardIcon, "theme-color-primary")}
                    />
                  </Card.Content>
                </Card>
                <Card
                  data-cy="transition-form-property-industry"
                  className={classnames(Styles.card, "card-border")}
                  onClick={() => {
                    change("propertyType", COMPANY);
                    setCurrentStep({ ...currentStep, stage: OUT });
                    GoogleTagManager.shortOfferPropertyTypeClick(COMPANY);
                  }}
                >
                  <Card.Content className={Styles["card-content"]}>
                    <Card.Header textAlign={"center"} className={Styles["card-header"]}>
                      {t("blogArticleUnfolding:formFieldDescriptors.propertyType.company")}
                    </Card.Header>
                    <Icon
                      name="industry"
                      size={"huge"}
                      className={classnames(Styles.cardIcon, "theme-color-primary")}
                    />
                  </Card.Content>
                </Card>
              </div>
            </div>
          </div>
        </Transition>
        <Transition
          {...transitionProps}
          reactKey={CATEGORY}
          visible={currentStep.step === CATEGORY && currentStep.stage === IN}
          onHide={() => {
            setCurrentStep(getNextStep(currentStep));
          }}
        >
          <div className={Styles.flexGrow}>
            <div className={Styles.stepContent}>
              <h3 className={Styles.title}>
                {t("blogArticleUnfolding:formFieldDescriptors.categoryType.description")}
              </h3>
              <div className={Styles.cards}>
                <Card
                  data-cy="transition-form-category-electricity"
                  className={classnames(Styles.card, "card-border")}
                  onClick={() => {
                    onCategoryChange && onCategoryChange(CategoryTypes.ELECTRICITY);
                    setCurrentStep({ ...currentStep, stage: OUT });
                    GoogleTagManager.shortOfferCategoryClick("ELECTRICITY");
                  }}
                >
                  <Card.Content className={Styles["card-content"]}>
                    <Card.Header textAlign={"center"} className={Styles["card-header"]}>
                      {t("common:electricity")}
                    </Card.Header>
                    <CategoryIcon.Icon
                      name="strom"
                      size={"huge"}
                      className={classnames(Styles.cardIcon, "theme-color-primary")}
                    />
                  </Card.Content>
                </Card>
                <Card
                  data-cy="transition-form-category-gas"
                  className={classnames(Styles.card, "card-border")}
                  onClick={() => {
                    onCategoryChange && onCategoryChange(CategoryTypes.GAS);
                    setCurrentStep({ ...currentStep, stage: OUT });
                    GoogleTagManager.shortOfferCategoryClick("GAS");
                  }}
                >
                  <Card.Content className={Styles["card-content"]}>
                    <Card.Header textAlign={"center"} className={Styles["card-header"]}>
                      {t("common:gas")}
                    </Card.Header>
                    <CategoryIcon.Icon
                      name={"gas"}
                      size={"huge"}
                      className={classnames(Styles.cardIcon, "theme-color-primary")}
                    />
                  </Card.Content>
                </Card>
              </div>
            </div>
          </div>
        </Transition>
        <Transition
          {...transitionProps}
          reactKey={LOCATION}
          visible={currentStep.step === LOCATION && currentStep.stage === IN}
          onHide={() => {
            setCurrentStep(getNextStep(currentStep));
          }}
        >
          <div className={Styles.flexGrow}>
            <div className={Styles.stepContent}>
              <h3 className={Styles.title}>
                {t("blogArticleUnfolding:formFieldDescriptors.postalcode.description", {
                  categoryName: t(`categories:${categoryType || ""}`),
                })}
              </h3>
              <div className={Styles.centerContent}>
                <PostalCodeAndLocation
                  t={t} // Required or breaks
                  change={change} // Required or breaks
                  profile={false} // It creates 2 rows with labels
                  resetSection={resetSection} // Required by the component
                  forceMobile={true}
                  compact={true}
                  required={true}
                  categorySlug={categoryEndpoint}
                />
              </div>
            </div>
          </div>
        </Transition>
        <Transition
          {...transitionProps}
          reactKey={CONSUMPTION}
          visible={currentStep.step === CONSUMPTION && currentStep.stage === IN}
          onHide={() => {
            setCurrentStep(getNextStep(currentStep));
          }}
        >
          <div className={Styles.flexGrow}>
            <div className={Styles.stepContent}>
              <h3 className={Styles.title}>
                {t("blogArticleUnfolding:formFieldDescriptors.electricityYearlyConsumption.description")}
              </h3>
              <div className={Styles.centerContent}>
                <ConsumptionField
                  t={t} // Required or breaks
                  categoryType={categoryType} // Required to show buttons
                  name={consumptionFieldName} // Required for placeholder
                  unfolding={false} // To show additional title
                  native={true} // To make buttons grey
                  forceMobile={true}
                  propertyType={currentValues.propertyType}
                  change={change} // to fix flow errors, is this necessary?
                />
              </div>
            </div>
          </div>
        </Transition>
        <Transition
          {...transitionProps}
          reactKey={PROVIDER}
          visible={currentStep.step === PROVIDER && currentStep.stage === IN}
          onHide={() => {
            if (userRef.current.user) {
              memoizedHandleAuthenticate(userRef.current.user.id, true);
            } else {
              setCurrentStep(getNextStep(currentStep));
            }
          }}
        >
          <div className={Styles.flexGrow}>
            <div className={Styles.stepContent}>
              <h3 className={Styles.title}>
                {t("blogArticleUnfolding:formFieldDescriptors.currentProvider.description", {
                  categoryName: t("categories:" + (categoryType || "")),
                })}
              </h3>
              <div className={Styles.centerContent}>
                <ProviderSelectField
                  name="companyId" // Required for the field
                  name2="tariffId" // Required for tariff field
                  t={t} // Required or breaks
                  messageOnTop={false} // Required to show message
                  providersLoaded={Object.keys(providers.providers).length !== 0} // Required to show correct message on top
                  categorySlug={categorySlug} // Required to make request
                  change={change} // Required or breaks
                  providersLoading={providers.isFetching} // Not required, just to see the loader
                  native={true} // Not required, to show tooltip
                  unfolding={true} // Not required, to change labels
                  forceMobile={true}
                  isCompany={isCompany}
                />
                <RadioBtnsEcoTariff
                  t={t} // Required or breaks
                  name={ecoTariffFieldName} // Required for ReduxForm
                  no="NO_ECO_TARIFF" // Required to save value
                  yes={YES_ECO_TARIFF}
                  forceMobile={true}
                  native={false}
                />
              </div>
            </div>
          </div>
        </Transition>
        <Transition
          {...transitionProps}
          reactKey={REGISTRATION}
          visible={currentStep.step === REGISTRATION && currentStep.stage === IN}
          onHide={() => {
            setCurrentStep(getNextStep(currentStep));
          }}
        >
          <IframeRegisterStep
            t={t} // Required or breaks
            stackButtons={isMobile()}
            showSpinner={false}
            lite={false}
            onRegister={memoizedHandleAuthenticate}
            isAnon={isAnon}
          />
        </Transition>
        <Transition
          {...transitionProps}
          reactKey={CONFIRMATION}
          visible={currentStep.step === CONFIRMATION && currentStep.stage === IN}
          onShow={() => {
            GoogleTagManager.shortOfferSuccess();
          }}
        >
          <ConfirmationMessage iframe showLoginMessage={user.user?.newUser === undefined} />
        </Transition>
        {showNextButton && (
          <>
            <Divider />
            <Button
              primary={valid && !pristine && !disableNextButton}
              disabled={invalid || pristine || disableNextButton}
              fluid
              data-cy="submitButton"
              onClick={() => setCurrentStep({ ...currentStep, stage: OUT })}
            >
              {currentStep.step === PROVIDER
                ? t("blogArticleUnfolding:submitButtonLate")
                : t("blogArticleUnfolding:submitButtonEarly")}
            </Button>
          </>
        )}
      </Segment>
      <Segment className={Styles.shortOfferfooter}>
        <div className={Styles["stepper-wrapper"]}>
          <Step.Group fluid unstackable size="mini" data-cy="steps" className={Styles.stepGroup}>
            {Object.keys(STEPS).map((step, index, array) => {
              return (
                <Step
                  key={index}
                  active={array.indexOf(currentStep.step) + 1 > index}
                  className={classnames(Styles["stepper"], {
                    [Styles["stepper-first"]]: index === 0,
                    [Styles["stepper-last"]]: index === array.length - 1,
                  })}
                />
              );
            })}
          </Step.Group>
        </div>
        <List horizontal>
          <List.Item target="_blank" href={`https://www.remind.me${InternalDataProtectionRoute}`}>
            {t("footer:dataProtection")}
          </List.Item>
          <List.Item target="_blank" href={`https://www.remind.me${InternalImpressumRoute}`}>
            {t("footer:impressum")}
          </List.Item>
          <List.Item target="_blank" href={`https://www.remind.me${InternalUserSomethingRoute}`}>
            {t("footer:termsOfUse")}
          </List.Item>
        </List>
      </Segment>
    </Segment.Group>
  );
}

export { IframeTransitionForm };
